<script setup lang="ts">
import { URL_IMAGE } from '@/config/constants'
import { languageIdBus } from '@/event/languageIdBus'
import type { Option } from '@/storage/products'
import { getOptionValueNameById } from '@/utils/product/getOptionValueNameById'
import ButtonSelectOptionValueHappy from './ButtonSelectOptionValueHappy.vue'
import { computed, ref, watch } from 'vue'
import { selectOptionsListHappy } from '@/happyTheme/event/product/selectOptionsListHappy'

const props = defineProps<{
    name_option: string
    options: Option
}>()

const maxOptionCount = computed(() => {
    if (!props.options || !props.options.values) return 0
    const propsOption = props.options

    const resetOption = propsOption.values
        .filter((o) => o.reset == 1)
        .map((o) => o.option_value_id)

    let totalResult = 0

    if (
        (propsOption.free_option_count === 0 && propsOption.max_option_count === 0) ||
        (propsOption.free_option_count !== 0 && propsOption.max_option_count === 0)
    ) {
        totalResult = propsOption.values.length - resetOption.length
    }
    if (propsOption.max_option_count !== 0) {
        totalResult = propsOption.max_option_count
    }

    return totalResult
})

const selectedValuesPaid = ref<Record<string, { total: number; price: string }>>({})

const hasSelectedCheckbox = computed(() => {
    const optionId = props.options.option_id

    if (
        !selectOptionsListHappy.value[optionId]?.selected_values &&
        !selectOptionsListHappy.value[optionId]?.selected_values?.checkbox_option
    ) {
        return props.options.values.map((o) => o.option_value_id)
    }
    const checkboxSelected =
        selectOptionsListHappy.value[optionId].selected_values.checkbox_option

    if (!checkboxSelected) return []

    const optionProps = props.options
    const informationProps = optionProps.values.map((o) => o.option_value_id)

    return informationProps
})

const getCurrentQuantity = (optionValueId: number): number => {
    const optionValue = props.options.values.find(
        (v) => v.option_value_id === optionValueId
    )
    const optionData = selectOptionsListHappy.value[props.options.option_id]
    if (!optionValue || !optionData || !optionData.selected_values?.checkbox_option)
        return 0

    const allSelected = [
        ...optionData.selected_values.checkbox_option.option_value_free,
        ...optionData.selected_values.checkbox_option.option_value_paid
    ]

    const hasGroupReset = allSelected.some(
        (v) => v.option_value_reset === 1 && v.option_value_grup === optionValue.grup
    )

    if (hasGroupReset) return 0

    const count = allSelected.filter((v) => v.option_value_id === optionValueId).length

    return count
}

watch(
    () => selectOptionsListHappy.value[props.options.option_id]?.selected_values,
    (newValue) => {
        if (!newValue) return

        if (newValue && newValue.checkbox_option.option_value_paid.length !== 0) {
            selectedValuesPaid.value = newValue.checkbox_option.option_value_paid.reduce(
                (acc, option) => {
                    const id = option.option_value_id
                    const price = option.option_value_price

                    if (acc[id]) {
                        acc[id].total += 1
                    } else {
                        acc[id] = {
                            total: 1,
                            price: price
                        }
                    }

                    return acc
                },
                {} as Record<string, { total: number; price: string }>
            )
        } else {
            selectedValuesPaid.value = {}
        }
    },
    { deep: true, immediate: true }
)
</script>

<template>
    <section class="section-option-value-happy">
        <div
            class="div-option-value-happy"
            v-if="props.options"
            v-for="(option_value, index) in props.options.values"
            :class="{
                'border-b border-border-gray-happy-light':
                    index !== props.options.values.length - 1
            }"
            :key="option_value.option_value_id"
        >
            <div class="div-option-value-description-happy">
                <div class="div-info-option-value-happy">
                    <img
                        loading="lazy"
                        class="img-option-value-happy"
                        :src="`${URL_IMAGE}${option_value.image}`"
                        alt=""
                    />
                    <span> - </span>
                    <div class="price-container">
                        <p class="text-option-value-happy">
                            {{
                                getOptionValueNameById(
                                    option_value.option_value_id,
                                    languageIdBus
                                )
                            }}
                        </p>

                        <p
                            class="whitespace-nowrap text-border-red-happy-light"
                            v-if="
                                props.options.type === 'radio' &&
                                parseFloat(option_value.ov_price).toFixed(2) !== '0.00'
                            "
                        >
                            ({{ parseFloat(option_value.ov_price).toFixed(2) + ' €' }})
                        </p>
                        <p
                            class="whitespace-nowrap text-border-red-happy-light"
                            v-if="
                                (props.options.type === 'checkbox' ||
                                    props.options.type === 'select') &&
                                selectedValuesPaid[option_value.option_value_id] &&
                                parseFloat(
                                    selectedValuesPaid[option_value.option_value_id]
                                        ?.price || '0'
                                ).toFixed(2) !== '0.00'
                            "
                        >
                            ({{
                                (
                                    parseFloat(option_value.ov_price) *
                                        selectedValuesPaid[option_value.option_value_id]
                                            ?.total || 0
                                ).toFixed(2) + ' €'
                            }})
                        </p>
                    </div>
                </div>
                <div class="button-container">
                    <ButtonSelectOptionValueHappy
                        :key="option_value.option_value_id"
                        v-if="props.options.type === 'radio'"
                        :current-option-value-id="option_value.option_value_id"
                        :option="props.options"
                        :max-quantity="1"
                        :current-quantity="
                            selectOptionsListHappy[props.options.option_id]
                                ?.selected_values?.radio_option &&
                            selectOptionsListHappy[props.options.option_id]
                                ?.selected_values?.radio_option.option_value_id ===
                                option_value.option_value_id
                                ? 1
                                : 0
                        "
                    />

                    <ButtonSelectOptionValueHappy
                        v-if="
                            hasSelectedCheckbox.includes(option_value.option_value_id) &&
                            props.options.type === 'select'
                        "
                        :key="option_value.option_value_id"
                        :current-option-value-id="option_value.option_value_id"
                        :option="props.options"
                        :max-quantity="maxOptionCount"
                        :current-quantity="
                            getCurrentQuantity(option_value.option_value_id)
                        "
                    />
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.section-option-value-happy {
    @apply flex flex-col;
}
.div-option-value-happy {
    @apply flex items-center min-w-full gap-2;
}

.div-option-value-description-happy {
    @apply flex items-center min-w-full justify-between px-2;
}

.div-info-option-value-happy {
    @apply flex items-center gap-2 w-full min-h-12;
}

.img-option-value-happy {
    @apply min-w-12 max-w-12 min-h-12 max-h-12 object-contain;
}
.text-option-value-happy {
    @apply text-wrap w-full;
}

.button-container {
    @apply flex items-center shrink-0;
}
.price-container {
    @apply flex items-center flex-wrap;
}
</style>
